import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, ShieldAlert, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';

const REASON_LABELS = {
  inappropriate: 'Inappropriate or offensive',
  spam_misleading: 'Spam or misleading',
  harassment_harmful: 'Harassment or harmful',
  something_else: 'Something else',
};

const OUTCOME_LABELS = {
  no_action: 'No action',
  removed_from_explore: 'Removed from Explore',
  closed_no_longer_explore: 'Closed — no longer in Explore',
  closed_content_deleted: 'Closed — content deleted',
  closed_reported_account_deleted: 'Closed — account deleted',
};

const RETENTION_REASONS = [
  { value: 'unresolved_complaint', label: 'Unresolved complaint' },
  { value: 'legal_requirement', label: 'Legal requirement' },
  { value: 'legal_proceedings_contemplated', label: 'Legal proceedings contemplated' },
  { value: 'legal_safety_other', label: 'Other legal or safety purpose' },
];

const inSixMonths = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 6);
  return d.toISOString().slice(0, 10);
};

const RetentionControls = ({ report, busy, onChange }) => {
  const [reason, setReason] = useState(report.exceptionalRetentionReason || '');
  const [reviewDate, setReviewDate] = useState(inSixMonths());
  const held = report.retentionState === 'exceptional';

  if (report.status === 'open') return null;

  const submit = (action) => {
    const body =
      action === 'end'
        ? {}
        : { reason, nextRetentionReviewAt: new Date(`${reviewDate}T12:00:00Z`).toISOString() };
    onChange(report.id, action, body);
  };

  return (
    <div className="mod-retention" data-testid={`retention-controls-${report.id}`}>
      <p className="mod-meta">
        {held ? (
          <>
            On legal hold ({RETENTION_REASONS.find((r) => r.value === report.exceptionalRetentionReason)?.label
              || report.exceptionalRetentionReason}). Next review{' '}
            {report.nextRetentionReviewAt
              ? new Date(report.nextRetentionReviewAt).toLocaleDateString()
              : '—'}
            . Renewed {report.exceptionalRetentionRenewals} time
            {report.exceptionalRetentionRenewals === 1 ? '' : 's'}.
          </>
        ) : (
          <>
            Deletes{' '}
            {report.deleteAfter ? new Date(report.deleteAfter).toLocaleDateString() : '—'} (12
            months after closure).
          </>
        )}
      </p>

      {report.retentionReviewOverdue && (
        <p className="mod-overdue-line" data-testid={`retention-overdue-${report.id}`}>
          This review is overdue. End the hold or renew it deliberately.
        </p>
      )}

      <div className="mod-retention-fields">
        <select
          className="mod-select"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-label="Exceptional retention reason"
          data-testid={`retention-reason-${report.id}`}
        >
          <option value="">Choose a reason…</option>
          {RETENTION_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <input
          type="date"
          className="mod-select"
          value={reviewDate}
          onChange={(e) => setReviewDate(e.target.value)}
          aria-label="Next retention review date"
          data-testid={`retention-review-date-${report.id}`}
        />
      </div>

      <div className="mod-actions">
        {!held ? (
          <button
            className="limit-modal-btn secondary"
            disabled={busy || !reason}
            onClick={() => submit('enter')}
            data-testid={`retention-enter-${report.id}`}
          >
            Place legal hold
          </button>
        ) : (
          <>
            <button
              className="limit-modal-btn secondary"
              disabled={busy || !reason}
              onClick={() => submit('renew')}
              data-testid={`retention-renew-${report.id}`}
            >
              Renew hold
            </button>
            <button
              className="limit-modal-btn primary"
              disabled={busy}
              onClick={() => submit('end')}
              data-testid={`retention-end-${report.id}`}
            >
              End hold
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const Moderation = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState('open');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (next) => {
    setLoading(true);
    try {
      setData(await api.getModerationReports(next));
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user?.canModerate) return;
    load(status);
  }, [authLoading, user, status, load]);

  const decide = async (reportId, outcome) => {
    setBusyId(reportId);
    try {
      const res = await api.closeModerationReport(reportId, outcome);
      toast.success(OUTCOME_LABELS[res.outcome] || 'Report closed');
      await load(status);
    } catch (err) {
      toast.error('Could not close this report');
    } finally {
      setBusyId(null);
    }
  };

  const lift = async (recId) => {
    setBusyId(recId);
    try {
      await api.liftModerationRestriction(recId);
      toast.success('Restriction lifted', {
        description: 'It stays with people you trust until the author chooses Everyone again.',
      });
      await load(status);
    } catch (err) {
      toast.error('Could not lift the restriction');
    } finally {
      setBusyId(null);
    }
  };

  const retention = async (reportId, action, body) => {
    setBusyId(reportId);
    try {
      await api.manageRetentionHold(reportId, action, body);
      toast.success(
        action === 'end' ? 'Hold ended' : action === 'renew' ? 'Hold renewed' : 'Legal hold placed',
        {
          description:
            action === 'end'
              ? 'The original 12-month deletion date has been restored.'
              : 'A deliberate review is required to extend it again.',
        },
      );
      await load(status);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update the hold');
    } finally {
      setBusyId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="page" data-testid="moderation-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  if (!user?.canModerate) {
    return (
      <div className="page" data-testid="moderation-page">
        <div className="placeholder" data-testid="moderation-not-found">
          <h2 className="placeholder-title">Not found</h2>
          <button className="placeholder-btn" onClick={() => navigate('/')}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="moderation-page">
      <header className="feed-header">
        <button className="back-btn inline" onClick={() => navigate('/settings')} aria-label="Back" data-testid="moderation-back">
          <ArrowLeft />
        </button>
        <h1 className="feed-greeting">Moderation</h1>
        <p className="feed-sub">Reported recommendations only. Reporters are never named.</p>
      </header>

      {data?.overdueRetentionReviews > 0 && (
        <div className="moderation-overdue" data-testid="moderation-overdue-badge">
          <Clock className="moderation-overdue-icon" />
          {data.overdueRetentionReviews} retention review
          {data.overdueRetentionReviews === 1 ? '' : 's'} overdue
        </div>
      )}

      <div className="filter-bar">
        <div className="chip-row">
          {['open', 'closed', 'all'].map((s) => (
            <button
              key={s}
              className={`chip ${status === s ? 'active' : ''}`}
              onClick={() => setStatus(s)}
              data-testid={`moderation-filter-${s}`}
            >
              {s === 'open' ? 'Open' : s === 'closed' ? 'Closed' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      ) : !data ? (
        <div className="placeholder" data-testid="moderation-error">
          <p className="placeholder-text">We could not load the queue.</p>
          <button className="placeholder-btn" onClick={() => load(status)}>Try again</button>
        </div>
      ) : data.items.length === 0 ? (
        <div className="placeholder" data-testid="moderation-empty">
          <ShieldAlert className="placeholder-icon" />
          <h2 className="placeholder-title">Nothing to review</h2>
          <p className="placeholder-text">No reports with this status.</p>
        </div>
      ) : (
        <div className="feed-list" data-testid="moderation-list">
          {data.items.map((r) => (
            <article className="mod-card" key={r.id} data-testid={`moderation-report-${r.id}`}>
              <div className="mod-card-head">
                <span className="mod-reason" data-testid={`moderation-reason-${r.id}`}>
                  {REASON_LABELS[r.reason] || r.reason}
                </span>
                <span className="mod-status">{r.status === 'open' ? 'Open' : OUTCOME_LABELS[r.outcome] || 'Closed'}</span>
              </div>

              <h3 className="mod-snapshot-name">{r.snapshot?.name}</h3>
              <p className="mod-snapshot-reason">{r.snapshot?.reason}</p>
              <p className="mod-meta">
                {r.snapshot?.category} · reported {new Date(r.createdAt).toLocaleDateString()}
                {r.contentStillExists
                  ? ` · now ${r.currentVisibility === 'everyone' ? 'in Explore' : 'people you trust'}`
                  : ' · content deleted'}
              </p>

              {r.editedAfterReporting && r.currentContent && (
                <div className="mod-edited" data-testid={`moderation-edited-${r.id}`}>
                  <strong>Edited after reporting.</strong> Now: “{r.currentContent.name}” —{' '}
                  {r.currentContent.reason}
                </div>
              )}

              {r.moderationRestricted && (
                <div className="mod-restricted" data-testid={`moderation-restricted-${r.id}`}>
                  Restricted: the author cannot choose Everyone until this is lifted.
                </div>
              )}

              {r.status === 'open' && (
                <div className="mod-actions">
                  <button
                    className="limit-modal-btn secondary"
                    disabled={busyId === r.id}
                    onClick={() => decide(r.id, 'no_action')}
                    data-testid={`moderation-no-action-${r.id}`}
                  >
                    No action
                  </button>
                  <button
                    className="limit-modal-btn primary"
                    disabled={busyId === r.id}
                    onClick={() => decide(r.id, 'removed_from_explore')}
                    data-testid={`moderation-remove-${r.id}`}
                  >
                    Remove from Explore
                  </button>
                </div>
              )}

              {r.status !== 'open' && r.moderationRestricted && (
                <div className="mod-actions">
                  <button
                    className="limit-modal-btn secondary"
                    disabled={busyId === r.recommendationId}
                    onClick={() => lift(r.recommendationId)}
                    data-testid={`moderation-lift-${r.recommendationId}`}
                  >
                    Lift restriction
                  </button>
                </div>
              )}

              <RetentionControls report={r} busy={busyId === r.id} onChange={retention} />
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
